import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';

import { SeriesTimelineRow } from '../../CalendarEvents/util/seriesTimeline';
import { SeriesOutcomeEnum } from '../../../store/series/types';
import { resolveDotColor } from '../lib/dotColor';
import { CardWarning, DotColor } from '../types';
import styles from './SeriesDots.module.scss';

const SLOT_WIDTH = 28;

const COLOR_CLASS: Record<DotColor, string> = {
  green: styles.dot_green,
  gray: styles.dot_gray,
  red: styles.dot_red,
  purple: styles.dot_purple,
  orange: styles.dot_orange,
  yellow: styles.dot_yellow,
  dollarGreen: styles.dot_dollarGreen,
};

type SeriesDotsProps = {
  rows: SeriesTimelineRow[];
  currentEventId: string | null;
  seriesOutcome: SeriesOutcomeEnum | undefined;
  cardWarning: CardWarning | null;
};

type SeriesDotProps = {
  color: DotColor;
  isCurrent: boolean;
  isFirstConsultation: boolean;
};

function SeriesDot({ color, isCurrent, isFirstConsultation }: SeriesDotProps) {
  const dotClassNames = [styles.dot, COLOR_CLASS[color]];
  if (isCurrent) {
    dotClassNames.push(styles.dot_current);
  }

  return (
    <div className={styles.slot}>
      <div className={dotClassNames.join(' ')}>
        {isFirstConsultation ? '1' : <RefreshIcon className={styles.icon} />}
      </div>
    </div>
  );
}

export function SeriesDots({ rows, currentEventId, seriesOutcome, cardWarning }: SeriesDotsProps) {
  // Trailing placeholder when the series is still open and is flagged as
  // missing a follow-up. Renders as an extra red dot at the end of the
  // sequence so it visually completes the timeline ("the next slot is
  // missing"). Hidden when the series is closed (an outcome was set) since
  // no further follow-ups are expected.
  const showMissingFollowupDot = cardWarning === 'followupMissing' && seriesOutcome === undefined;

  if (rows.length === 0 && !showMissingFollowupDot) {
    return null;
  }

  const totalSlots = rows.length + (showMissingFollowupDot ? 1 : 0);
  const connectorWidth = (totalSlots - 1) * SLOT_WIDTH;

  const dots: React.ReactNode[] = [];
  for (let rowsIndex = 0; rowsIndex < rows.length; rowsIndex += 1) {
    const row = rows[rowsIndex];
    const isCurrent = row.event.id === currentEventId;

    const color = resolveDotColor({
      variant: row.variant,
      isPast: row.isPast,
      eventStatus: row.event.status,
      isCurrent,
      seriesOutcome,
    });

    dots.push(
      <SeriesDot
        key={row.event.id}
        color={color}
        isCurrent={isCurrent}
        isFirstConsultation={row.isFirstConsultation}
      />,
    );
  }

  if (showMissingFollowupDot) {
    dots.push(
      <SeriesDot
        key="missing-followup"
        color="red"
        isCurrent={false}
        isFirstConsultation={false}
      />,
    );
  }

  return (
    <div className={styles.container}>
      {totalSlots > 1 && <div className={styles.connector} style={{ width: `${connectorWidth}px` }} />}
      {dots}
    </div>
  );
}
